import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ArrowLeft,
  Loader2,
  User,
  CreditCard,
  BadgeCheck,
  Mail,
  CalendarClock,
  FileSearch,
  Check,
  Lock,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const INTAKE_KEY = "ea_intake_id";

const inputCls =
  "bg-black/50 border-white/20 text-white placeholder:text-neutral-600 rounded-none h-12 focus-visible:ring-gold focus-visible:ring-offset-0 focus-visible:border-gold/60";

const STEPS = [
  { num: "01", label: "Your Details", icon: User },
  { num: "02", label: "Intake Fee", icon: CreditCard },
  { num: "03", label: "You're In", icon: BadgeCheck },
];

const OWNER_EMAIL = "Info@elevatedapexco.com";
const SCHEDULING_URL = "#schedule-consultation";

const DOCS = [
  { type: "drivers_license", label: "Driver's License", hint: "Front of your current, valid license" },
  { type: "ssn_card", label: "Social Security Card", hint: "A clear photo or scan of your card" },
  { type: "utility_bill", label: "Utility Bill", hint: "Dated within the last 60 days, showing your address" },
];

const Stepper = ({ step }) => (
  <div className="grid grid-cols-3 border border-white/10" data-testid="intake-stepper">
    {STEPS.map((s, i) => {
      const active = step === i + 1;
      const done = step > i + 1;
      return (
        <div
          key={s.num}
          className={`flex items-center gap-3 px-4 lg:px-6 py-4 ${i < 2 ? "border-r border-white/10" : ""} ${
            active ? "bg-gold/5" : ""
          }`}
          data-testid={`stepper-step-${s.num}`}
        >
          <span
            className={`w-8 h-8 shrink-0 flex items-center justify-center border font-mono text-xs ${
              done
                ? "border-gold bg-gold text-black"
                : active
                ? "border-gold text-gold"
                : "border-white/15 text-neutral-600"
            }`}
          >
            {done ? <Check className="w-4 h-4" /> : s.num}
          </span>
          <div className="hidden sm:block">
            <p className={`font-mono text-[9px] uppercase tracking-[0.2em] ${active ? "text-gold" : "text-neutral-600"}`}>
              Step {s.num}
            </p>
            <p className={`text-xs ${active ? "text-white" : "text-neutral-500"}`}>{s.label}</p>
          </div>
        </div>
      );
    })}
  </div>
);

const StepDetails = ({ onDone }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/intake`, form);
      localStorage.setItem(INTAKE_KEY, data.id);
      onDone(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="intake-details-form">
      <div className="space-y-2">
        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Full legal name</Label>
        <Input required value={form.name} onChange={set("name")} placeholder="Jordan Ellis" className={inputCls} data-testid="intake-name-input" />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Email</Label>
          <Input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="intake-email-input" />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Phone — for your consultation call</Label>
          <Input required value={form.phone} onChange={set("phone")} placeholder="(555) 010-2244" className={inputCls} data-testid="intake-phone-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Goals (optional)</Label>
        <Textarea value={form.notes} onChange={set("notes")} placeholder="Home purchase, funding, score goals..." className={`${inputCls} min-h-[90px]`} data-testid="intake-notes-input" />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="group w-full flex items-center justify-center gap-3 bg-gold text-black text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
        data-testid="intake-details-submit"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue to Payment <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></>}
      </button>
    </form>
  );
};

const StepPayment = ({ intakeId, onBack }) => {
  const [loading, setLoading] = useState(false);

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "intake_fee",
        origin_url: window.location.origin,
        intake_id: intakeId,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="intake-payment-step">
      <div className="border border-white/10 bg-black/40 p-8 flex items-center justify-between gap-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Client Intake & Onboarding</p>
          <p className="mt-3 text-sm text-neutral-400 font-light leading-relaxed max-w-md">
            Covers your full three-bureau report analysis, personal elevation plan,
            and your first private coaching session.
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-serif text-5xl text-white">$300</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">one-time</p>
        </div>
      </div>
      <div className="mt-6 flex items-center gap-3 text-neutral-500">
        <Lock className="w-3.5 h-3.5 text-gold shrink-0" />
        <p className="text-xs font-light">Secure checkout powered by Stripe. You'll return here automatically after payment.</p>
      </div>
      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <button
          onClick={onBack}
          className="flex items-center justify-center gap-2 border border-white/20 text-neutral-300 text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:border-white/50 transition-colors duration-300"
          data-testid="intake-payment-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={pay}
          disabled={loading}
          className="group flex-1 flex items-center justify-center gap-3 bg-gold text-black text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
          data-testid="intake-pay-button"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay $300 Securely <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></>}
        </button>
      </div>
    </div>
  );
};

const ThankYou = () => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: EASE }}
    className="text-center py-10"
    data-testid="intake-thank-you"
  >
    <div className="flex items-center gap-5">
      <span className="w-12 h-12 border border-gold rotate-45 flex items-center justify-center shrink-0">
        <Check className="w-5 h-5 text-gold -rotate-45" />
      </span>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
          Payment confirmed — $300 intake fee
        </p>
        <p className="mt-1.5 text-sm text-neutral-400 font-light">
          Welcome aboard. Three quick things left, all right here.
        </p>
      </div>
    </div>

    <div className="mt-5 border border-white/10 bg-black/40 p-8" data-testid="thankyou-smartcredit-card">
      <div className="flex items-center gap-3">
        <FileSearch className="w-5 h-5 text-gold" />
        <h3 className="font-serif text-xl text-white tracking-tight">Pull your credit reports</h3>
      </div>
      <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed">
        Start SmartCredit's $1 seven-day trial to access all three bureau reports and scores —
        we'll use them together in your action plan, so grab them before your call.
      </p>
      <a
        href="https://www.smartcredit.com"
        target="_blank"
        rel="noopener noreferrer"
        className="group mt-6 inline-flex items-center gap-3 border border-gold text-gold text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:bg-gold hover:text-black transition-colors duration-300"
        data-testid="thankyou-smartcredit-link"
      >
        Start the $1 SmartCredit Trial
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </a>
    </div>

    <div className="mt-5 border border-white/10 bg-black/40 p-8" data-testid="thankyou-email-card">
      <div className="flex items-center gap-3">
        <Mail className="w-5 h-5 text-gold" />
        <h3 className="font-serif text-xl text-white tracking-tight">Email us your documents</h3>
      </div>
      <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed">
        Send clear photos or scans of all three documents to:
      </p>
      <a
        href={`mailto:${OWNER_EMAIL}?subject=${encodeURIComponent("New Client Intake — Your Full Name")}`}
        className="mt-3 inline-block font-serif text-2xl sm:text-3xl text-gold hover:text-white transition-colors duration-300 break-all"
        data-testid="thankyou-email-link"
      >
        {OWNER_EMAIL}
      </a>
      <ul className="mt-7 space-y-3">
        {DOCS.map((d) => (
          <li key={d.type} className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rotate-45 bg-gold/70 shrink-0 mt-2" />
            <div>
              <p className="text-sm text-white">{d.label}</p>
              <p className="text-xs text-neutral-500 font-light">{d.hint}</p>
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-7 pt-4 border-t border-white/10 text-xs text-neutral-500 font-light leading-relaxed">
        Tip: use the subject line "New Client Intake — Your Full Name" so we can match
        your documents to your file right away.
      </p>
    </div>

    <div className="mt-5 border border-white/10 bg-black/40 p-8" data-testid="thankyou-schedule-card">
      <div className="flex items-center gap-3">
        <CalendarClock className="w-5 h-5 text-gold" />
        <h3 className="font-serif text-xl text-white tracking-tight">Schedule your phone consultation</h3>
      </div>
      <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed">
        Pick a time for your 30-minute call — we'll review your goals and map your first 90 days.
      </p>
      <a
        href={SCHEDULING_URL}
        className="group mt-6 inline-flex items-center gap-3 bg-gold text-black text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:bg-white transition-colors duration-300"
        data-testid="thankyou-schedule-link"
      >
        Book Your Consultation Call
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </a>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 leading-relaxed">
        Online scheduling is coming online shortly — if the link doesn't open yet,
        we'll email you available times within one business day
      </p>
    </div>

    <Link
      to="/"
      className="mt-10 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 hover:text-gold transition-colors duration-300"
      data-testid="intake-back-home-link"
    >
      <ArrowLeft className="w-4 h-4" /> Back to home
    </Link>
  </motion.div>
);

export default function Intake() {
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [intakeId, setIntakeId] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const payment = params.get("payment");
    const sessionId = params.get("session_id");
    const stored = localStorage.getItem(INTAKE_KEY);

    if (payment === "success" && sessionId) {
      setVerifying(true);
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setVerifying(false);
            setStep(3);
            setIntakeId(stored);
            setParams({}, { replace: true });
            toast.success("Payment confirmed — welcome aboard.");
            return;
          }
        } catch (e) {
          // keep polling briefly
        }
        attempts += 1;
        if (attempts < 15) {
          setTimeout(poll, 2000);
        } else {
          setVerifying(false);
          toast.error("We couldn't confirm your payment yet. If you were charged, contact us.");
          setStep(2);
          setIntakeId(stored);
          setParams({}, { replace: true });
        }
      };
      poll();
    } else if (payment === "canceled") {
      toast.info("Checkout canceled — you can resume payment whenever you're ready.");
      setStep(stored ? 2 : 1);
      setIntakeId(stored);
      setParams({}, { replace: true });
    } else if (stored) {
      setIntakeId(stored);
      axios
        .get(`${API}/intake/${stored}`)
        .then(({ data }) => {
          if (data.status === "paid") {
            setStep(3);
          } else {
            setStep(2);
          }
        })
        .catch(() => localStorage.removeItem(INTAKE_KEY));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headings = {
    1: <>Tell us where<br /><span className="italic text-gold">to reach you.</span></>,
    2: <>Secure your<br /><span className="italic text-gold">onboarding.</span></>,
    3: <>Payment <span className="italic text-gold">confirmed.</span></>,
  };

  return (
    <main className="bg-ink-base min-h-screen" data-testid="intake-page">
      <Navbar />
      <section className="pt-36 pb-24 lg:pb-32">
        <div className="max-w-3xl mx-auto px-6">
          {verifying ? (
            <div className="text-center py-20" data-testid="intake-verifying-payment">
              <Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" />
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-400">
                Confirming your payment with Stripe...
              </p>
            </div>
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE }}
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold" data-testid="intake-overline">
                  Client Intake — Elevated Apex Co.
                </p>
                <h1 className="mt-6 font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-white" data-testid="intake-heading">
                  {headings[step]}
                </h1>
              </motion.div>

              <div className="mt-12">
                <Stepper step={step} />
              </div>

              <div className="mt-10 bg-ink-surface border border-white/10 p-8 lg:p-12">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.5, ease: EASE }}
                  >
                    {step === 1 && (
                      <StepDetails
                        onDone={(id) => {
                          setIntakeId(id);
                          setStep(2);
                        }}
                      />
                    )}
                    {step === 2 && intakeId && <StepPayment intakeId={intakeId} onBack={() => setStep(1)} />}
                    {step === 3 && <ThankYou />}
                  </motion.div>
                </AnimatePresence>
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
